import Container from "@/components/Container/Container";
import Section from "@/components/Section/Section";
import PageHero from "@/components/PageHero/PageHero";
import CTASection from "@/components/CTASection/CTASection";
import Button from "@/components/Button/Button";
import { site } from "@/content/site";
import styles from "./FeesPage.module.css";

const rates = [
  {
    label: "Individual therapy",
    detail: "53-minute session",
    price: "$150",
  },
  {
    label: "Couples therapy",
    detail: "53–90 minute session",
    price: "$220 – $350",
  },
  {
    label: "Ketamine-Assisted Psychotherapy (KAP)",
    detail: "Per session",
    price: "$400",
  },
  {
    label: "Clinical supervision",
    detail: "Rates vary by format (individual or consultation)",
    price: "$80 – $150",
  },
];

const headwayBenefits = [
  "Verify insurance coverage before scheduling",
  "Understand copays and deductibles in advance",
  "Access secure scheduling and billing",
  "Available through Headway and Alma",
];

const details = [
  {
    eyebrow: "Out-of-network benefits",
    title: "Superbills available on request.",
    body: "If I'm not in-network with your insurance plan, I can provide a superbill — a detailed receipt you can submit to your provider for possible reimbursement under your out-of-network benefits. Clients are encouraged to confirm reimbursement eligibility directly with their insurance.",
  },
  {
    eyebrow: "Payment",
    title: "Due at the time of service.",
    body: "Payment is collected at the time of service through the secure client portal. If you have questions about coverage, private-pay rates, or supervision fees, we can discuss them during an initial consultation.",
  },
];

export default function FeesPage() {
  return (
    <>
      <PageHero
        eyebrow="Fees & insurance"
        title="Clear, transparent options for getting started."
        intro="I accept insurance through Headway and Alma, and offer private-pay rates for individual therapy, couples work, KAP, and supervision."
        accent="bloom"
      />

      <Section spacing="default">
        <Container width="default" className={styles.insurance}>
          <div className={styles.insuranceCopy}>
            <span className="eyebrow">In-network insurance</span>
            <h2 className={styles.insuranceTitle}>A simpler way to use your benefits.</h2>
            <p>
              I accept insurance through both Headway and Alma — two platforms that handle
              the administrative side of insurance so therapy stays focused on your care.
              Both platforms verify benefits, process claims, and manage billing on your
              behalf. You can use whichever portal works best for you.
            </p>
            <div className={styles.insuranceActions}>
              <Button as="a" href={site.links.headway} variant="primary" size="lg">
                Verify insurance via Headway
              </Button>
              <Button as="a" href={site.links.alma} variant="secondary" size="lg">
                Book via Alma
              </Button>
            </div>
            <p className={styles.note}>
              Not sure which to use? Either portal will show whether your insurance is
              accepted and what your expected costs will be before your first appointment.
            </p>
          </div>
          <aside className={styles.benefits} aria-label="Through Headway or Alma, clients can">
            <span className="eyebrow">Through Headway or Alma</span>
            <ul className={styles.benefitsList}>
              {headwayBenefits.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </aside>
        </Container>
      </Section>

      <Section tone="soft" spacing="loose">
        <Container width="default">
          <div className={styles.privatePayHeader}>
            <span className="eyebrow">Private pay</span>
            <h2>For clients who prefer to pay privately.</h2>
            <p>
              The following rates apply for clients paying out-of-pocket or whose insurance
              isn't accepted through Headway or Alma.
            </p>
          </div>
          <ul className={styles.rates}>
            {rates.map((r) => (
              <li key={r.label} className={styles.rateRow}>
                <div className={styles.rateLabel}>
                  <span className={styles.rateName}>{r.label}</span>
                  <span className={styles.rateDetail}>{r.detail}</span>
                </div>
                <span className={styles.ratePrice}>{r.price}</span>
              </li>
            ))}
          </ul>
        </Container>
      </Section>

      <Section spacing="default">
        <Container width="default">
          <div className={styles.detailsHeader}>
            <span className="eyebrow">Good to know</span>
            <h2>A few practical details.</h2>
          </div>
          <div className={styles.detailsGrid}>
            {details.map((d) => (
              <article key={d.eyebrow} className={styles.detailCard}>
                <span className="eyebrow">{d.eyebrow}</span>
                <h3 className={styles.detailTitle}>{d.title}</h3>
                <p>{d.body}</p>
              </article>
            ))}
          </div>
        </Container>
      </Section>

      <CTASection />
    </>
  );
}
